import { useState } from "react";
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useMeters, useAnomalies } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
  StatCard,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function AnomalyPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);
  const [z, setZ] = useState(2.5);

  const { data, isLoading } = useAnomalies(meterId || meters[0], days, z);

  const all = data?.data ?? [];
  const warnings = all.filter((d: any) => d.severity === "warning").length;
  const criticals = all.filter((d: any) => d.severity === "critical").length;
  const curSpikes = all.filter(
    (d: any) => d.is_anomaly && d.trigger?.includes("Current"),
  ).length;

  const lineData = all.map((d: any) => ({
    ts: fmt.dt(d.timestamp),
    kw: d.kw,
    anom: d.is_anomaly ? d.kw : null,
  }));

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Anomaly Detection"
          subtitle="Z-score spike detection on active power and phase current"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <DaysSelect value={days} onChange={setDays} />
          <select
            className="ra-select-sm"
            value={z}
            onChange={(e) => setZ(Number(e.target.value))}
          >
            {[1.5, 2, 2.5, 3, 3.5].map((v) => (
              <option key={v} value={v}>
                Z ≥ {v}
              </option>
            ))}
          </select>
        </PageHeader>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Anomalies"
            value={data?.total_anomalies ?? 0}
            color="red"
          />
          <StatCard label="Warnings" value={warnings} color="amber" />
          <StatCard label="Critical Spikes" value={criticals} color="red" />
          <StatCard label="Current Spikes" value={curSpikes} color="violet" />
        </div>

        <ChartCard
          title="Active Power with Anomaly Markers"
          subtitle="Red dots = flagged intervals"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart
                data={lineData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="ts"
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="ra-tooltip">
                        <p className="text-slate-500 mb-1">{label}</p>
                        <p className="font-mono text-blue-600">
                          KW: {payload[0]?.value?.toFixed(2)} W
                        </p>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="kw"
                  stroke={CHART_COLORS.blue}
                  dot={false}
                  strokeWidth={1.5}
                  name="KW"
                />
                <Scatter
                  dataKey="anom"
                  fill={CHART_COLORS.red}
                  r={4}
                  name="Anomaly"
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {criticals + warnings > 0 && (
          <div className="ra-card mt-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-1">
              Flagged Intervals
              <span className="ml-2 text-xs font-normal text-slate-400">
                — sorted by severity, max 50 shown
              </span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-200">
                    {[
                      "Timestamp",
                      "KW (W)",
                      "Current (A)",
                      "Z-Score KW",
                      "Z-Score Current",
                      "Trigger",
                      "Severity",
                    ].map((h) => (
                      <th key={h} className="text-left pb-2 pr-4 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {all
                    .filter((d: any) => d.is_anomaly)
                    .sort((a: any, b: any) => {
                      const order: Record<string, number> = {
                        critical: 0,
                        warning: 1,
                        normal: 2,
                      };
                      return (
                        (order[a.severity] ?? 2) - (order[b.severity] ?? 2)
                      );
                    })
                    .slice(0, 50)
                    .map((d: any, i: number) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-2 pr-4 font-mono text-slate-600">
                          {fmt.dt(d.timestamp)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-800 font-semibold">
                          {fmt.num(d.kw, 2)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-cyan-600">
                          {fmt.num(d.current, 3)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-slate-600">
                          {fmt.num(d.z_score_kw, 3)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-violet-600">
                          {fmt.num(d.z_score_current, 3)}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={
                              d.trigger?.includes("Current") &&
                              d.trigger?.includes("Power")
                                ? "ra-badge-red"
                                : d.trigger?.includes("Current")
                                  ? "ra-badge-violet"
                                  : "ra-badge-yellow"
                            }
                          >
                            {d.trigger}
                          </span>
                        </td>
                        <td className="py-2">
                          <span
                            className={
                              d.severity === "critical"
                                ? "ra-badge-red"
                                : "ra-badge-yellow"
                            }
                          >
                            {d.severity}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
